import React, { FC, useEffect } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom';
import M24FormEditor from '@/editor/M24FormEditor/M24FormEditor';
import CardsView from '@/views/CardsView/CardsView';
import SiteLocationsSettings from '@/views/SettingsView/SiteLocationsSettings/SiteLocationsSettings';
import SiteSharingSettings from '@/views/SettingsView/SiteSharingSettings/SiteSharingSettings';
import SiteUsersSettings from '@/views/SettingsView/SiteUsersSettings/SiteUsersSettings';
import SiteKeywordsSettings from '@/views/SettingsView/SiteKeywordsSettings/SiteKeywordsSettings';
import { SettingsView } from '@/views/SettingsView/SettingsView';
import { StatisticsView } from '@/views/StatisticsView/StatisticsView';
import { SiteCardCategoriesSettings } from '@/views/SettingsView/CardCategoriesView/SiteCardCategoriesSettings';
import { Settings } from '@/Settings';
import EkulturCallbackHandler from '@/components/EkulturLoginHandler/EkulturCallbackHandler';
import EkulturLogoutHandler from '@/components/EkulturLoginHandler/EkulturLogoutHandler';
import { DashboardView } from '@/views/DashboardView/DashboardView';
import { AdminLayout } from '@/views/AdminView/AdminLayout';
import { AdminDashboard } from '@/views/AdminView/AdminDashboard';
import { AdminOwners } from '@/views/AdminView/AdminOwners';
import { AdminSites } from '@/views/AdminView/AdminSites';
import { AdminDesign } from '@/views/AdminView/AdminDesign';
import { AdminStatistics } from '@/views/AdminView/AdminStatistics';
import { AdminToolsView } from '@/views/AdminView/AdminToolsView';
import AdminUsersView from '@/views/AdminView/AdminUsersView';
import AdminAnalysisAndStatisticsView from '@/views/AdminView/AdminAnalysisAndStatisticsView';
import AdminTestToolsView from '@/components/Admin/AdminTestTools/AdminTestToolsView';
import AnalyticsView from '@/views/AnalyticsView/AnalyticsView';
import NotFoundView from './views/NotFoundView';
import ContentView from './views/ContentView/ContentView';
import MediaView from './views/MediaView/MediaView';
import FormView from './views/FormView/FormView';
import WebpageSettingsView from './views/SettingsView/WebpageSettingsView/WebpageSettingsView';
import MainLayout from './components/MainLayout';
import OwnerSettingsView from './views/OwnerSettingsView';
import Editor from './editor/Editor';
import { EditorContext } from './editor/lib/declarations/EditorContext';
import EmployeesView from './views/EmployeesView/EmployeesView';
import StylesView from './views/StylesView';
import SiteCategoriesSettings from './views/SettingsView/SiteCategoriesSettings';
import LanguageSettingsView from './views/LanguageSettingsView/LanguageSettingsView';
import { useStore } from './components/store/Store';
import LanguageCreationView from './views/LanguageSettingsView/LanguageCreationView/LanguageCreationView';
import LanguageLogoSettings from './views/LanguageSettingsView/LanguageLogoSettings';
import LanguageMenuSettings from './views/LanguageSettingsView/LanguageMenuSettings/LanguageMenuSettings';
import LanguageFooterSettings from './views/LanguageSettingsView/LanguageFooterSettings/LanguageFooterSettings';
import LanguageInfoSettings from './views/LanguageSettingsView/LanguageInfoSettings';
import LanguageSEOSettings from './views/LanguageSettingsView/LanguageSEOSettings';
import Language404Settings from './views/LanguageSettingsView/Language404Settings';
import LanguageKeypagesSettings from './views/LanguageSettingsView/LanguageKeypagesSettings';
import { ContentViewContentContext } from './views/ContentView/ContentViewContext';
import { FormDataView } from './views/FormView/FormData/FormDataView';
import EkulturLoginController from './components/EkulturLoginHandler/EkulturLoginController';
import { SitePageTemplateSettings } from './views/SettingsView/SitePageTemplateSettings/SitePageTemplateSettings';

const environmentLine = (top: string, bottom: string) => {
  const environmentColor = 'rgb(252, 8, 237)';
  let environmentLineHeight = '0px';
  let title = 'You are in the production environment';
  if (!window.location.href.includes('https://admin2.museum24.no/')) {
    // environmentColor = '#98c3f7';
    environmentLineHeight = '8px';
    title = 'You are in the development environment';
  }
  return (
    <div
      title={title}
      style={{
        position: 'absolute',
        top,
        bottom,
        left: 0,
        right: 0,
        zIndex: 9999,
        height: environmentLineHeight,
        backgroundColor: environmentColor,
      }}
    />
  );
};

const Plausible: FC = () => {
  const { state } = useStore();
  const location = useLocation();

  useEffect(() => {
    // Ikke initialiser script før selectedSite er satt
    if (!state.selectedSite || (!state.selectedSite.name && !state.selectedSite.id)) {
      return;
    }
    const script = document.getElementById('PlausibleScript');
    if (!script) {
      const screenWidth = window.screen.width;
      const screenHeight = window.screen.height;
      const screenDensity = Math.round(window.devicePixelRatio * 10) / 10;
      const screenDimension = `${screenWidth}x${screenHeight}`;
      const screenDimensionWithDensity = `${screenWidth}×${screenHeight} px (DPR: ${screenDensity})`;
      console.log('Initializing Plausible with screen dimension:', screenDimensionWithDensity);
      const newScript = document.createElement('script');
      newScript.defer = true;
      newScript.setAttribute('id', 'PlausibleScript');
      newScript.setAttribute('event-site', state.selectedSite.name || state.selectedSite.id?.toString() || 'not-set');
      newScript.setAttribute('event-screen', screenDimension);
      newScript.setAttribute('event-density', screenDimensionWithDensity);
      newScript.dataset.domain = 'admin2.museum24.no';
      newScript.src = 'https://plausible.io/js/script.pageview-props.manual.js';
      document.head.appendChild(newScript);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.plausible = function plausible(...args) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        (window.plausible.q = window.plausible.q || []).push(args);
      };
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.plausible('pageview', {
        props: {
          site: state.selectedSite.name || state.selectedSite.id?.toString(),
          screen: screenDimension,
          density: screenDimensionWithDensity,
        },
      });
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (window.plausible) {
      // console.log('track pageview:', location.pathname);
      // Seems like this way of tracking "property:site" does not have any effect
      script?.setAttribute('event-site', state.selectedSite?.name || state.selectedSite?.id?.toString() || '-');
      // So tries a manual pageview tracking instead..:
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.plausible('pageview', {
        props: { site: state.selectedSite?.name || state.selectedSite?.id?.toString() || '-' },
      });
    }
  }, [location, state.selectedSite?.id, state.selectedSite?.name]);

  return null;
};
const Root: FC = () => {
  const { state } = useStore();
  const params = useParams<{ designId?: string }>();

  const languageRoutes = state.selectedSite?.site_contents?.map((content) => {
    return (
      <Route key={content.id} path={content.locale} element={<Outlet />}>
        <Route index element={<Navigate to='languageInfo' />} />
        <Route
          path='languageInfo'
          element={
            <LanguageSettingsView locale={content.locale}>
              <LanguageInfoSettings locale={content.locale} />
            </LanguageSettingsView>
          }
        />
        <Route
          path='seo'
          element={
            <LanguageSettingsView locale={content.locale}>
              <LanguageSEOSettings />
            </LanguageSettingsView>
          }
        />
        <Route
          path='404'
          element={
            <LanguageSettingsView locale={content.locale}>
              <Language404Settings />
            </LanguageSettingsView>
          }
        />
        <Route
          path='logo'
          element={
            <LanguageSettingsView locale={content.locale}>
              <LanguageLogoSettings />
            </LanguageSettingsView>
          }
        />
        <Route
          path='menu'
          element={
            <LanguageSettingsView locale={content.locale}>
              <LanguageMenuSettings />
            </LanguageSettingsView>
          }
        />
        <Route
          path='footer'
          element={
            <LanguageSettingsView locale={content.locale}>
              <LanguageFooterSettings />
            </LanguageSettingsView>
          }
        />
        <Route
          path='keypages'
          element={
            <LanguageSettingsView locale={content.locale}>
              <LanguageKeypagesSettings />
            </LanguageSettingsView>
          }
        />
      </Route>
    );
  });

  return (
    <>
      <Plausible />
      <Routes>
        <Route
          path='/'
          element={
            <>
              {environmentLine('0', 'auto')}
              <MainLayout>
                <Outlet />
              </MainLayout>
              {environmentLine('auto', '0')}
            </>
          }>
          <Route index element={<Navigate to='/dashboard' />} />
          <Route path='dashboard' element={<DashboardView />} />
          <Route path='m24admin' element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path='owners' element={<AdminOwners />} />
            <Route path='sites' element={<AdminSites />} />

            <Route path='design' element={<Outlet />}>
              <Route index element={<AdminDesign />} />
            </Route>
            <Route path='analyze' element={<AdminAnalysisAndStatisticsView />} />
            <Route path='statistics' element={<AdminStatistics />} />
            <Route path='superadmins' element={<AdminUsersView />} />
            <Route path='tools' element={<AdminToolsView />} />
            <Route path='test-tools' element={<AdminTestToolsView />} />
          </Route>
          <Route
            path='content'
            element={<ContentView contentContext={ContentViewContentContext.PAGE} key='content' />}
          />
          <Route
            path='events'
            element={<ContentView contentContext={ContentViewContentContext.EVENT} key='events' />}
          />
          <Route path='employees' element={<EmployeesView />} />
          <Route path='cards' element={<CardsView />} />
          <Route path='media' element={<MediaView />} />
          <Route path='forms' element={<Outlet />}>
            <Route index element={<FormView />} />
            <Route path='data/:formId' element={<FormDataView />} />
          </Route>
          <Route path='settings' element={<SettingsView />}>
            <Route index element={<Navigate to='webpage' />} />
            <Route path='styles' element={<StylesView />} />
            <Route path='webpage' element={<WebpageSettingsView />} />
            <Route path='owner' element={<OwnerSettingsView />} />
            <Route path='sharing' element={<SiteSharingSettings />} />
            <Route path='locations' element={<SiteLocationsSettings />} />
            <Route path='users' element={<SiteUsersSettings />} />
            <Route path='keywords' element={<SiteKeywordsSettings />} />
            <Route path='categories' element={<SiteCategoriesSettings />} />
            <Route path='card-categories' element={<SiteCardCategoriesSettings />} />
            <Route path='page-templates' element={<SitePageTemplateSettings />} />
          </Route>
          <Route path='language-settings' element={<Outlet />}>
            <Route index element={<Navigate to='create' />} />
            {languageRoutes}
            <Route path='create' element={<LanguageCreationView />} />
          </Route>
          <Route path='statistics' element={<StatisticsView />} />
          <Route path='analytics' element={<AnalyticsView />} />
        </Route>
        <Route path='/' element={<Outlet />}>
          <Route path='formEditor'>
            <Route path=':formId' element={<M24FormEditor />} />
          </Route>
        </Route>
        <Route path='*' element={<NotFoundView />} />
      </Routes>
    </>
  );
};

const EkulturLoginControllerWrapper: FC = () => {
  return (
    <EkulturLoginController>
      <Outlet />
    </EkulturLoginController>
  );
};

const router = createBrowserRouter([
  {
    path: Settings.EKULTUR_LOGIN_CALLBACK_PATH,
    element: <EkulturCallbackHandler />,
  },
  {
    path: '/logout',
    element: <EkulturLogoutHandler />,
  },
  {
    path: '/',
    element: <EkulturLoginControllerWrapper />,
    children: [
      {
        index: true,
        element: <Navigate to='dashboard' />,
      },
      {
        path: 'editor',
        children: [
          {
            path: ':modelId',
            element: (
              <>
                {environmentLine('0', 'auto')}
                <Plausible />
                <Editor context={EditorContext.PAGE} />
                {environmentLine('auto', '0')}
              </>
            ),
          },
        ],
      },
      {
        path: '*',
        element: <Root />,
      },
    ],
  },
]);

export const App = () => {
  return <RouterProvider router={router} />;
};

// export default App;
